import React, { useState, useEffect } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";

import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";

import { CiCircleMinus, CiCirclePlus } from "react-icons/ci";
import { IoArrowDownCircleOutline, IoArrowUpCircleOutline, IoPrintOutline } from "react-icons/io5";
import { FiDownload } from "react-icons/fi";
import { AiOutlineFullscreen } from "react-icons/ai";

export const CustomPDFViewer = ({ fileUrl, onPageChange }) => {
    const toolbarPluginInstance = toolbarPlugin();
    const { Toolbar } = toolbarPluginInstance;
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [seenPages, setSeenPages] = useState(new Set());

    // Resetear cuando cambia el archivo
    useEffect(() => {
        setCurrentPage(0);
        setTotalPages(0);
        setSeenPages(new Set());
    }, [fileUrl]);

    // Rastrear páginas vistas
    useEffect(() => {
        if (onPageChange && currentPage > 0 && totalPages > 0) {
            setSeenPages(prev => {
                const newSeenPages = new Set(prev);
                newSeenPages.add(currentPage);
                // Notificar al componente padre sobre las páginas vistas
                onPageChange(Array.from(newSeenPages), totalPages);
                return newSeenPages;
            });
        }
    }, [currentPage, totalPages, onPageChange]);

    return (
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            {fileUrl ? (
                <div
                    style={{
                        display: "flex",
                        height: "100%",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    {/* Toolbar flotante - solo mostrar cuando el PDF esté cargado */}
                    {totalPages > 0 && (
                        <div
                            style={{
                                alignItems: "center",
                                backgroundColor: "#eeeeee",
                                border: "1px solid rgba(0, 0, 0, 0.2)",
                                borderRadius: "2px",
                                bottom: "20px",
                                display: "flex",
                                left: "50%",
                                padding: "4px",
                                position: "absolute",
                                transform: "translate(-50%, 0)",
                                zIndex: 1,
                                gap: "6px",
                            }}
                        >
                        <Toolbar>
                            {(slots) => {
                                const {
                                    CurrentPageInput,
                                    Download,
                                    EnterFullScreen,
                                    GoToNextPage,
                                    GoToPreviousPage,
                                    NumberOfPages,
                                    Print,
                                    ZoomIn,
                                    ZoomOut,
                                } = slots;

                                return (
                                    <>
                                        {/* Zoom Out */}
                                        <ZoomOut>
                                            {({ onClick }) => (
                                                <button
                                                    type="button"
                                                    onClick={onClick}
                                                    style={{
                                                        border: "none",
                                                        background: "transparent",
                                                        padding: 0,
                                                        cursor: "pointer",
                                                        fontSize: "22px",
                                                    }}
                                                    aria-label="Zoom out"
                                                    title="Zoom out"
                                                >
                                                    <CiCircleMinus />
                                                </button>
                                            )}
                                        </ZoomOut>

                                        {/* Zoom In */}
                                        <ZoomIn>
                                            {({ onClick }) => (
                                                <button
                                                    type="button"
                                                    onClick={onClick}
                                                    style={{
                                                        border: "none",
                                                        background: "transparent",
                                                        padding: 0,
                                                        cursor: "pointer",
                                                        fontSize: "22px",
                                                    }}
                                                    aria-label="Zoom in"
                                                    title="Zoom in"
                                                >
                                                    <CiCirclePlus />
                                                </button>
                                            )}
                                        </ZoomIn>

                                        <div style={{ width: 10 }} />

                                        {/* Prev page */}
                                        <GoToPreviousPage>
                                            {({ onClick, isDisabled }) => (
                                                <button
                                                    type="button"
                                                    onClick={onClick}
                                                    disabled={isDisabled}
                                                    style={{
                                                        border: "none",
                                                        background: "transparent",
                                                        padding: 0,
                                                        cursor: isDisabled ? "not-allowed" : "pointer",
                                                        fontSize: "22px",
                                                        opacity: isDisabled ? 0.4 : 1,
                                                    }}
                                                    aria-label="Previous page"
                                                    title="Previous page"
                                                >
                                                    <IoArrowUpCircleOutline />
                                                </button>
                                            )}
                                        </GoToPreviousPage>

                                        {/* Page input */}
                                        <div style={{ width: "4rem" }}>
                                            <CurrentPageInput />
                                        </div>

                                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            <span>/</span>
                                            <NumberOfPages />
                                        </div>

                                        {/* Next page */}
                                        <GoToNextPage>
                                            {({ onClick, isDisabled }) => (
                                                <button
                                                    type="button"
                                                    onClick={onClick}
                                                    disabled={isDisabled}
                                                    style={{
                                                        border: "none",
                                                        background: "transparent",
                                                        padding: 0,
                                                        cursor: isDisabled ? "not-allowed" : "pointer",
                                                        fontSize: "22px",
                                                        opacity: isDisabled ? 0.4 : 1,
                                                    }}
                                                    aria-label="Next page"
                                                    title="Next page"
                                                >
                                                    <IoArrowDownCircleOutline />
                                                </button>
                                            )}
                                        </GoToNextPage>

                                        <div style={{ width: 10 }} />

                                        {/* Fullscreen */}
                                        <EnterFullScreen>
                                            {({ onClick }) => (
                                                <button
                                                    type="button"
                                                    onClick={onClick}
                                                    style={{
                                                        border: "none",
                                                        background: "transparent",
                                                        padding: 0,
                                                        cursor: "pointer",
                                                        fontSize: "20px",
                                                    }}
                                                    aria-label="Fullscreen"
                                                    title="Fullscreen"
                                                >
                                                    <AiOutlineFullscreen />
                                                </button>
                                            )}
                                        </EnterFullScreen>

                                        {/* Download */}
                                        <Download>
                                            {({ onClick }) => (
                                                <button
                                                    type="button"
                                                    onClick={onClick}
                                                    style={{
                                                        border: "none",
                                                        background: "transparent",
                                                        padding: 0,
                                                        cursor: "pointer",
                                                        fontSize: "20px",
                                                    }}
                                                    aria-label="Download"
                                                    title="Download"
                                                >
                                                    <FiDownload />
                                                </button>
                                            )}
                                        </Download>

                                        {/* Print */}
                                        <Print>
                                            {({ onClick }) => (
                                                <button
                                                    type="button"
                                                    onClick={onClick}
                                                    style={{
                                                        border: "none",
                                                        background: "transparent",
                                                        padding: 0,
                                                        cursor: "pointer",
                                                        fontSize: "20px",
                                                    }}
                                                    aria-label="Print"
                                                    title="Print"
                                                >
                                                    <IoPrintOutline />
                                                </button>
                                            )}
                                        </Print>
                                    </>
                                );
                            }}
                        </Toolbar>
                        </div>
                    )}

                    <div style={{ flex: 1, overflow: "hidden" }}>
                        <Viewer 
                            fileUrl={fileUrl} 
                            plugins={[toolbarPluginInstance]}
                            onDocumentLoad={(e) => {
                                const numPages = e.doc.numPages;
                                setTotalPages(numPages);
                                // Marcar la primera página como vista
                                if (numPages > 0 && onPageChange) {
                                    setCurrentPage(1);
                                    setSeenPages(new Set([1]));
                                    onPageChange([1], numPages);
                                }
                            }}
                            onPageChange={(e) => {
                                const pageNum = e.currentPage + 1; // +1 porque las páginas empiezan en 0
                                setCurrentPage(pageNum);
                            }}
                        />
                    </div>
                </div>
            ) : (
                <div style={{ padding: "20px", textAlign: "center" }}>Cargando PDF...</div>
            )}
        </Worker>
    );
};
